import React, { useEffect, useState } from "react";
import Layout, { Container } from "../components/layout";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { apiurl } from "../config/config";
import { getCookie } from "../config/webStorage";
import { ArrowLeft } from "lucide-react";

const OnlineApplicationDetails = () => {
  const { applicationObjId } = useParams();
  let navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [newRemarks, setNewRemarks] = useState("");

  useEffect(() => {
    const fetchApplication = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${apiurl}/api/online-application/get/${applicationObjId}`
        );
        setApplication(response.data.application);
        setLoading(false);
      } catch (err) {
        setError("Failed to fetch application details.");
        setLoading(false);
      }
    };

    fetchApplication();
  }, [applicationObjId]);

  const statusColors = {
    submitted: "bg-blue-100 text-blue-700",
    under_review: "bg-yellow-100 text-yellow-700",
    interview_scheduled: "bg-purple-100 text-purple-700",
    reviewed: "bg-indigo-100 text-indigo-700",
    shortlisted: "bg-green-100 text-green-700",
    rejected: "bg-red-100 text-red-700",
    hired: "bg-teal-100 text-teal-700",
  };

  if (loading) {
    return (
      <Layout>
        <Container>
          <p className="text-gray-500">Loading application details...</p>
        </Container>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Container>
          <p className="text-red-500">{error}</p>
        </Container>
      </Layout>
    );
  }

  if (!application) {
    return (
      <Layout>
        <Container>
          <p className="text-gray-500">Application not found.</p>
        </Container>
      </Layout>
    );
  }

  // Function to generate viewer URL (handles pdf/doc/docx)
  const getViewerUrl = (url) => {
    if (!url) return "";
    if (url.endsWith(".pdf")) {
      return url; // pdf can open directly
    }
    // for doc/docx use Google Docs viewer
    return `https://docs.google.com/gview?url=${encodeURIComponent(
      url
    )}&embedded=true`;
  };
  const backHandle = ()=>{
    navigate(`/online-applications/${application.jobId}`);
  }

  return (
    <Layout>
      <Container overflow="overflow-scroll">
        <div className="p-6 bg-gray-100 min-h-screen space-y-6">
        <div className="flex justify-start items-center">  
        <ArrowLeft className="mr-3 cursor-pointer" onClick={()=>backHandle()}/>
          <h2 className="text-2xl font-semibold"> Application Details</h2>
        </div>

          {/* Candidate Info Card */}
          <div className="bg-white p-6 rounded-2xl shadow space-y-4">
            <h3 className="text-xl font-semibold">
              Candidate: {application.name || application.candidateId}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-gray-500">Application ID</p>
                <p className="font-medium">{application.id}</p>
              </div>
              <div>
                <p className="text-gray-500">Job ID</p>
                <p className="font-medium">{application.jobId}</p>
              </div>
              <div>
                <p className="text-gray-500">Email</p>
                <p className="font-medium">{application.email}</p>
              </div>
              <div>
                <p className="text-gray-500">Phone</p>
                <p className="font-medium">{application.phone}</p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-gray-500">Submitted At</p>
                <p className="font-medium">
                  {new Date(application.submittedAt).toLocaleString()}
                </p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-gray-500">Status</p>
                <p className="font-medium capitalize">{application.status}</p>
              </div>
            </div>

            {/* Resume Button */}
            {application.resume && (
              <button
                onClick={() => setShowResumeModal(true)}
                className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg shadow hover:bg-green-700"
              >
                View Resume
              </button>
            )}
          </div>
          <div className="bg-white p-6 rounded-2xl shadow space-y-4">
            <h3 className="text-xl font-semibold">Update Status</h3>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await axios.post(
                    `${apiurl}/api/online-application/status`,
                    {
                      applicationObjId: application._id,
                      status: newStatus,
                      remarks: newRemarks || `${newStatus} status updated`,
                    },
                    { headers: { Authorization: getCookie("swtoken") } }
                  );
                  window.location.reload(); // refresh after update
                } catch (err) {
                  alert("Failed to update status");
                }
              }}
              className="space-y-4"
            >
              <select
                className="w-full border rounded-lg p-2"
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
              >
                  <option value="submitted">--Select Status--</option>
                {Object.keys(statusColors).map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
              <textarea
                className="w-full border rounded-lg p-2"
                placeholder="Add remarks..."
                value={newRemarks}
                onChange={(e) => setNewRemarks(e.target.value)}
              />
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700"
              >
                Update
              </button>
            </form>
          </div>

          {/* Answers Section */}
          <div className="bg-white p-6 rounded-2xl shadow space-y-4">
            <h3 className="text-xl font-semibold">Answers</h3>
            {application.answers && application.answers.length > 0 ? (
              <ul className="space-y-4">
                {application.answers.map((ans) => (
                  <li
                    key={ans._id}
                    className="p-4 border border-gray-200 rounded-lg"
                  >
                    <p className="text-gray-500 text-sm mb-1">Question:</p>
                    <p className="font-medium mb-2">{ans.questionText}</p>
                    <p className="text-gray-500 text-sm mb-1">Answer:</p>
                    <p>
                      {typeof ans.answer === "boolean"
                        ? ans.answer
                          ? "Yes"
                          : "No"
                        : ans.answer || "-"}
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500">No answers submitted.</p>
            )}
          </div>

          {/* History Section */}
          <div className="bg-white p-6 rounded-2xl shadow space-y-4">
            <h3 className="text-xl font-semibold">Action History</h3>
            {application.actionBy && application.actionBy.length > 0 ? (
              <ul className="space-y-4">
                {application.actionBy.map((action) => (
                  <li
                    key={action._id}
                    className="p-4 border border-gray-200 rounded-lg space-y-2"
                  >
                    <div className="flex justify-between items-center">
                      <p className="font-medium">{action.userName}</p>
                      <span className="text-sm text-gray-500">
                        {new Date(action.date).toLocaleString()}
                      </span>
                    </div>

                    {/* Status Transition */}
                    {action.oldStatus && action.newStatus && (
                      <div className="flex items-center space-x-2 text-sm">
                        <span
                          className={`px-2 py-1 rounded-full ${
                            statusColors[action.oldStatus] ||
                            "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {action.oldStatus}
                        </span>
                        <span>→</span>
                        <span
                          className={`px-2 py-1 rounded-full ${
                            statusColors[action.newStatus] ||
                            "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {action.newStatus}
                        </span>
                      </div>
                    )}

                    {/* Remarks */}
                    {action.remarks && (
                      <p className="text-gray-600 italic">“{action.remarks}”</p>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500">No actions recorded.</p>
            )}
          </div>
        </div>

        {/* Resume Modal */}
        {showResumeModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-4 rounded-xl shadow-xl max-w-5xl w-full h-[90vh] flex flex-col">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold">Candidate Resume</h3>
                <button
                  onClick={() => setShowResumeModal(false)}
                  className="px-3 py-1 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                >
                  Close
                </button>
              </div>
              <iframe
                src={getViewerUrl(application.resume)}
                title="Resume Preview"
                className="flex-1 w-full border rounded-lg"
              ></iframe>
            </div>
          </div>
        )}
      </Container>
    </Layout>
  );
};

export default OnlineApplicationDetails;